import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { Textarea } from '../../../../components/inputs/Textarea';
import type { ApiError } from '../../../../lib/apiError';
import type { JobPostingDraft } from '../utils/jobPostingDraft';
import type { RecruitmentUserOption } from '../vacancyTypes';

/**
 * The Job Posting stage's three fields (product call, 19 Oct 2026): **Posted by** — an account the
 * reader PICKS, not a stamp — **Posted at**, and **Remarks**. Shared by the tag form and its edit,
 * so the two cannot ask the question differently.
 *
 * The ids match the server's field names under `idPrefix`, so a rejected write focuses its field.
 */
export function JobPostingFields({
  idPrefix,
  draft,
  onChange,
  users,
  usersLoading,
  usersError,
  disabled,
  error,
}: {
  idPrefix: string;
  draft: JobPostingDraft;
  onChange: (next: JobPostingDraft) => void;
  users: RecruitmentUserOption[];
  usersLoading: boolean;
  usersError: string | null;
  disabled: boolean;
  error: ApiError | null;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          id={`${idPrefix}-posted-by-user-id`}
          label="Posted by"
          required
          description={usersError ?? 'The account that posted the vacancy — not necessarily whoever records it here.'}
          placeholder="Choose an account…"
          value={draft.postedByUserId}
          onChange={(value) => onChange({ ...draft, postedByUserId: value || null })}
          options={users.map((u) => ({ value: u.userId, label: u.displayName }))}
          loading={usersLoading}
          disabled={disabled}
          error={draft.postedByUserId ? error?.fieldError('postedByUserId') : 'Choose who posted it.'}
        />

        <DateField
          id={`${idPrefix}-posted-date`}
          label="Posted at"
          required
          description="The day the vacancy was posted."
          max={todayCalendarDate()}
          value={draft.postedDate}
          onChange={(value) => onChange({ ...draft, postedDate: value })}
          error={error?.fieldError('postedDate')}
          disabled={disabled}
        />
      </div>

      {/* A free note about the posting as a whole — full width, outside the grid. */}
      <Textarea
        id={`${idPrefix}-remarks`}
        label="Remarks"
        value={draft.remarks}
        onChange={(e) => onChange({ ...draft, remarks: e.target.value })}
        error={error?.fieldError('remarks')}
        disabled={disabled}
      />
    </>
  );
}
